import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, Calendar, Fuel, Gauge, Cog, Palette, Car, Hash, BadgeCheck, Search, ArrowLeft, AlertTriangle, Pencil } from "lucide-react";
import { api, photoUrl, formatApiError } from "../api";
import ScoreGauge from "../components/ScoreGauge";
import PaintMatrix from "../components/PaintMatrix";
import Timeline from "../components/Timeline";
import HistoryReportV2 from "../components/HistoryReportV2";
import { OWNERSHIP_V2 } from "../lib/ownership";
import { EVENT_META, fmtKm } from "../lib/labels";
import { autoVehicleImage, hasOwnPhoto } from "../lib/vehicleImages";

const SPEC_ICONS = { year: Calendar, engine: Cog, fuel: Fuel, transmission: Cog, km: Gauge, plate: Hash, color: Palette, body_type: Car };

const UNKNOWN_VIN_FLOW = true; // kullanici onayiyla canliya alindi (2026-06-25)

export default function Report() {
  const { vin } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setError("");
    api.get(`/vehicles/report/${encodeURIComponent(vin)}`)
      .then((r) => { if (!cancelled) setReport(r.data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); });
    return () => { cancelled = true; };
  }, [vin, api, formatApiError]);

  if (error) {
    if (!UNKNOWN_VIN_FLOW) {
      return (
        <div className="min-h-screen pt-28 pb-16 max-w-2xl mx-auto px-4">
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8 text-center animate-fade-up">
            <AlertTriangle className="w-10 h-10 text-amber-300 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-white">Kayıt Bulunamadı</h1>
            <p data-testid="report-error" className="mt-3 text-sm text-slate-300 leading-relaxed">{error}</p>
            <p className="mt-2 text-xs text-slate-500">Tramer / kilometre resmi veri kaynakları entegrasyonu yakında aktif olacaktır.</p>
            <Link to="/" className="inline-flex items-center gap-2 mt-6 text-cyan-300 hover:text-cyan-200 text-sm font-semibold">
              <ArrowLeft className="w-4 h-4" /> Yeni sorgulama yap
            </Link>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen pt-28 pb-16 max-w-2xl mx-auto px-4">
        <div data-testid="report-not-found" className="rounded-2xl border border-white/10 bg-slate-900/60 p-8 text-center animate-fade-up">
          <Car className="w-10 h-10 text-cyan-300 mx-auto" />
          <h1 className="mt-4 text-xl font-bold text-white">Bu Araç Henüz Kayıtlı Değil</h1>
          <p data-testid="report-error" className="mt-3 text-sm text-slate-300 leading-relaxed">
            Bu şasi numarasına ait araç şu anda Otokünye'de kayıtlı değil. Aracı sisteme kaydederek geçmiş bilgilerinin oluşturulmasına katkıda bulunabilirsiniz.
          </p>
          <p className="mt-2 text-xs text-slate-400 leading-relaxed">
            Aracın geçmiş kayıtları; hasar, kilometre, servis ve diğer bilgiler eklendikçe burada görüntülenebilir.
          </p>
          <Link data-testid="not-found-register-button" to={`/arac-kaydet?vin=${encodeURIComponent(vin || "")}`}
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl bg-cyan-400 text-slate-950 font-semibold hover:bg-cyan-300 transition-all text-sm">
            + Aracı Otokünye'ye Kaydet
          </Link>
          <p className="mt-6 text-[11px] text-slate-500">
            Not: Resmî Tramer ve kilometre veri entegrasyonları aktif olduğunda, ilgili resmî kayıtlar da sorgulanabilecektir.
          </p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 pt-16">
        <div className="w-12 h-12 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-400 text-sm">Rapor hazırlanıyor...</p>
      </div>
    );
  }

  const specs = [
    { key: "year", label: "Model Yılı", value: report.year },
    { key: "engine", label: "Motor", value: report.engine || "-" },
    { key: "fuel", label: "Yakıt Tipi", value: report.fuel || "-" },
    { key: "transmission", label: "Vites", value: report.transmission || "-" },
    { key: "km", label: "Kilometre", value: fmtKm(report.km) },
    { key: "plate", label: "Plaka", value: report.plate || "-" },
    { key: "color", label: "Renk", value: report.color || "-" },
    { key: "body_type", label: "Kasa Tipi", value: report.body_type || "-" },
  ];

  const statKeys = ["kaza", "hasar", "servis", "muayene", "parca_degisimi", "boya", "km_anomali"];
  const reportImg = hasOwnPhoto(report) ? photoUrl(report.photo || report.photos[0]) : autoVehicleImage(report.brand, report.model);

  return (
    <div data-testid="vehicle-report" className="min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Başlık */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <img data-testid="report-vehicle-image" src={reportImg} alt={`${report.brand} ${report.model}`}
              className="w-28 sm:w-36 h-20 sm:h-24 object-cover rounded-xl border border-cyan-500/30 shadow-md" />
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                {report.brand} <span className="text-slate-300">{report.model}</span>
              </h1>
            <div className="mt-3 inline-block font-mono-vin tracking-widest text-sm sm:text-base font-bold bg-slate-900 px-4 py-2 rounded-lg border border-cyan-500/30 text-cyan-300" data-testid="report-vin">
              {report.vin}
            </div>
            {report.verification_status && (
              <span data-testid="report-verification-badge" className={`ml-2 inline-block text-[11px] font-bold px-3 py-1.5 rounded-full border ${report.verification_status === "dogrulandi" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                {report.verification_status === "dogrulandi" ? "Ruhsat Doğrulandı" : "Ruhsat Doğrulaması: İnceleme Bekliyor"}
              </span>
            )}
            {report.first_registration && (
              <p className="mt-2 text-sm text-slate-500 flex items-center gap-1.5">
                <BadgeCheck className="w-4 h-4" /> İlk Tescil: {report.first_registration}
              </p>
            )}
            <p data-testid="report-ttl-note" className="mt-2 text-[11px] text-amber-300/80">Bu sorgulama kaydı 48 saat boyunca görüntülenebilir ve süre sonunda otomatik olarak silinir.</p>
            </div>
          </div>
          <div className="flex gap-3 no-print">
            {report.vehicle_id && (
              <Link data-testid="admin-edit-vehicle-button" to={`/araclarim/${report.vehicle_id}`}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-400 text-slate-950 font-semibold hover:bg-amber-300 transition-all text-sm">
                <Pencil className="w-4 h-4" /> Düzenle (Yönetici)
              </Link>
            )}
            <button data-testid="download-pdf-report-button" onClick={() => window.print()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/15 text-slate-200 hover:border-cyan-400/50 hover:text-cyan-300 transition-all text-sm">
              <Printer className="w-4 h-4" /> Raporu Yazdır / PDF
            </button>
            <Link data-testid="new-query-button" to="/"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-400 text-slate-950 font-semibold hover:bg-cyan-300 transition-all text-sm">
              <Search className="w-4 h-4" /> Yeni Sorgu
            </Link>
          </div>
        </div>

        {/* Özet + Skor */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 rounded-2xl border border-white/10 bg-slate-900/60 p-6 sm:p-8 backdrop-blur-xl">
            {report.photos && report.photos.length > 0 && (
              <img src={photoUrl(report.photos[0])} alt={`${report.brand} ${report.model}`}
                className="w-full h-56 sm:h-72 object-cover rounded-xl mb-6 border border-white/10" />
            )}
            <h2 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400">Araç Bilgileri</h2>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-5">
              {specs.map((s) => {
                const Icon = SPEC_ICONS[s.key];
                return (
                  <div key={s.key} className="flex flex-col gap-1">
                    <span className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Icon className="w-3.5 h-3.5" /> {s.label}
                    </span>
                    <span className="font-semibold text-slate-100">{s.value}</span>
                  </div>
                );
              })}
            </div>
            <h2 className="mt-8 text-xs font-mono-vin uppercase tracking-widest text-cyan-400">Kayıt Özeti</h2>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {statKeys.map((k) => (
                <span key={k} data-testid={`stat-${k}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${EVENT_META[k].ring} ${EVENT_META[k].color}`}>
                  {EVENT_META[k].label}: <b>{report.stats[k] || 0}</b>
                </span>
              ))}
            </div>
            {report.km_check && report.km_check.kontrol_edildi && (
              <div data-testid="km-check-card"
                className={`mt-5 rounded-xl border p-4 ${report.km_check.durum === "tespit_edildi" ? "border-red-500/40 bg-red-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`}>
                <p className={`text-sm font-extrabold ${report.km_check.durum === "tespit_edildi" ? "text-red-300" : "text-emerald-300"}`}>
                  {report.km_check.durum === "tespit_edildi" ? "🔴" : "🟢"} {report.km_check.ozet}
                </p>
                {report.km_check.bulgular.map((b, i) => (
                  <p key={i} data-testid={`km-check-finding-${i}`} className="mt-2 text-xs text-slate-300 leading-relaxed">{b.mesaj}</p>
                ))}
                <p className="mt-2 text-[11px] text-slate-500">
                  {report.km_check.karsilastirilan_kayit} kilometre kaydı tarih sırasına göre kronolojik olarak karşılaştırıldı.
                  {report.km_check.durum !== "tespit_edildi" && " Tüm kayıtlar arasında kilometre düşüşü bulunamadı."}
                </p>
              </div>
            )}
          </div>

          <div className="lg:col-span-4 rounded-2xl border border-white/10 bg-slate-900/60 p-6 sm:p-8 backdrop-blur-xl flex flex-col items-center justify-center">
            <h2 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400 self-start">Araç Geçmiş Skoru</h2>
            <div className="mt-6">
              <ScoreGauge score={report.score} label={report.score_label} />
            </div>
            <p className="mt-6 text-xs text-slate-500 text-center leading-relaxed">
              Skor; kaza, hasar, boya/değişen ve kilometre tutarlılığı dikkate alınarak otomatik hesaplanır.
            </p>
          </div>
        </div>

        {/* Boya Matrisi */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-6 sm:p-8 backdrop-blur-xl">
          <h2 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400 mb-5">Kaporta & Boya Durumu</h2>
          <PaintMatrix paint={report.paint} />
        </div>

        {/* Zaman Çizelgesi */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-6 sm:p-8 backdrop-blur-xl">
          {OWNERSHIP_V2 ? (
            <HistoryReportV2 vin={report.vin} />
          ) : (
            <>
              <h2 className="text-xs font-mono-vin uppercase tracking-widest text-cyan-400 mb-8">Araç Geçmişi — Zaman Çizelgesi</h2>
              {report.history.length > 0 ? (
                <Timeline history={report.history} />
              ) : (
                <p className="text-slate-500 text-sm">Bu araç için kayıt bulunmuyor.</p>
              )}
            </>
          )}
        </div>

        <p className="mt-8 text-xs text-slate-600 text-center">
          Bu rapor, sistemde kayıtlı araç verileriyle oluşturulmuştur. Tramer / kilometre resmi veri kaynakları entegrasyonu yakında eklenecektir.
        </p>
      </div>
    </div>
  );
}
